import { FileUploadDetailModal } from '@/components/admin/FileUploadDetailModal';
import { InsightAgentLogModal } from '@/components/admin/InsightAgentLogModal';
import { Modal } from '@/components/admin/Modal';
import { SavedQueryDetailModal } from '@/components/admin/SavedQueryDetailModal';
import { ArtifactPanel } from '@/components/artifact/ArtifactPanel';
import { Markdown } from '@/components/chat/Markdown';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import { ApiError, api } from '@/lib/api';
import { buildTokenTooltip } from '@/lib/chat-log-tooltip';
import { cn } from '@/lib/cn';
import type {
  AdminChatArtifactSummary,
  AdminChatInterrupt,
  AdminChatSessionSummary,
  AdminChatSessionTrace,
  AdminChatTurnError,
  Artifact,
  ChatMessage,
  ToolCallExternalRef,
} from '@innermate/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  AlertCircle,
  Archive,
  Bot,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronsDownUp,
  ChevronsUpDown,
  ClipboardList,
  Code,
  Copy,
  FileText,
  History,
  MessageSquare,
  Monitor,
  RefreshCw,
  ShieldBan,
  SlidersHorizontal,
  User,
  X,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ─── 턴 그루핑 유틸 ──────────────────────────────────────────────────────────

interface ChatTurn {
  kind: 'turn';
  index: number;
  userMessage: ChatMessage | null;
  iterations: ChatMessage[];
  finalAssistantMessage: ChatMessage | null;
  responseTimeMs: number | null;
  createdAt: string;
  hasError: boolean;
  isArchived: boolean;
  interrupts: AdminChatInterrupt[];
  artifacts: AdminChatArtifactSummary[];
  /** chat.turn_error audit_logs — LLM 스트림/run/첨부 ingest 단계 오류. createdAt 으로 이 턴에 매칭. */
  turnErrors: AdminChatTurnError[];
}

interface CompactionEvent {
  kind: 'compaction';
  id: string;
  createdAt: string;
  content: string;
  summarizedCount: number;
  summaryChars: number;
}

type TraceItem = ChatTurn | CompactionEvent;

function groupMessagesIntoTraceItems(
  messages: ChatMessage[],
  interrupts: AdminChatInterrupt[],
  artifacts: AdminChatArtifactSummary[],
  turnErrors: AdminChatTurnError[],
): TraceItem[] {
  const items: TraceItem[] = [];
  let current: ChatTurn | null = null;
  let turnIndex = 0;

  const push = () => {
    if (current) items.push(current);
    current = null;
  };

  const newTurn = (msg: ChatMessage): ChatTurn => ({
    kind: 'turn',
    index: turnIndex++,
    userMessage: null,
    iterations: [],
    finalAssistantMessage: null,
    responseTimeMs: null,
    createdAt: msg.createdAt,
    hasError: false,
    isArchived: msg.archivedAt != null,
    interrupts: [],
    artifacts: [],
    turnErrors: [],
  });

  for (const msg of messages) {
    if (msg.role === 'user') {
      push();
      current = newTurn(msg);
      current.userMessage = msg;
    } else if (msg.role === 'assistant') {
      if (!current) current = newTurn(msg);
      if (msg.archivedAt != null) current.isArchived = true;
      if (msg.toolCalls && msg.toolCalls.length > 0) {
        current.iterations.push(msg);
        if (msg.toolCalls.some((t) => t.status === 'error')) current.hasError = true;
      } else {
        current.finalAssistantMessage = msg;
        if (msg.responseTimeMs != null) current.responseTimeMs = msg.responseTimeMs;
      }
    } else if (msg.role === 'system') {
      const meta = (msg.metadata ?? {}) as {
        kind?: string;
        summarizedCount?: number;
        summaryChars?: number;
      };
      if (meta.kind === 'compaction') {
        push();
        items.push({
          kind: 'compaction',
          id: msg.id,
          createdAt: msg.createdAt,
          content: msg.content,
          summarizedCount: typeof meta.summarizedCount === 'number' ? meta.summarizedCount : 0,
          summaryChars:
            typeof meta.summaryChars === 'number' ? meta.summaryChars : msg.content.length,
        });
      }
    }
  }
  push();

  const turns = items.filter((it): it is ChatTurn => it.kind === 'turn');

  function findTurnFor(createdAt: string): ChatTurn | undefined {
    const first = turns[0];
    if (!first) return undefined;
    let target: ChatTurn = first;
    for (const t of turns) {
      if (new Date(t.createdAt) <= new Date(createdAt)) target = t;
      else break;
    }
    return target;
  }

  for (const intr of interrupts) {
    findTurnFor(intr.createdAt)?.interrupts.push(intr);
  }

  for (const art of artifacts) {
    findTurnFor(art.createdAt)?.artifacts.push(art);
  }

  for (const e of turnErrors) {
    const turn = findTurnFor(e.createdAt);
    if (turn) {
      turn.turnErrors.push(e);
      turn.hasError = true;
    }
  }

  return items;
}

/**
 * 턴 안 tool calls 중 첫 번째 `insight_agent` externalRef.
 * verify_tag 가 채움 — sequence 전체 row_id 공유.
 */
function getTurnExternalRef(turn: ChatTurn): ToolCallExternalRef | null {
  const allMsgs: ChatMessage[] = [...turn.iterations];
  if (turn.finalAssistantMessage) allMsgs.push(turn.finalAssistantMessage);
  for (const m of allMsgs) {
    if (!m.toolCalls) continue;
    for (const tc of m.toolCalls) {
      if (tc.externalRef?.kind === 'insight_agent') return tc.externalRef;
    }
  }
  return null;
}

// ─── 포맷 헬퍼 ───────────────────────────────────────────────────────────────

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  const totalSec = ms / 1000;
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = (totalSec % 60).toFixed(1);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/**
 * 토큰 수를 짧게 — 1234 → "1.2K", 12345 → "12.3K", 1234567 → "1.2M". 1000 미만은 그대로.
 * 경계 처리: 999_500 같이 K 표기로 1000 이상이 되는 경우 M 으로 승격해 "1000.0K" 같은 어색함 회피.
 */
function fmtTokens(n: number | null | undefined): string {
  if (n == null || n === 0) return '0';
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) {
    const k = n / 1000;
    if (k < 999.95) return `${k.toFixed(1)}K`;
    // 999_950 이상은 toFixed(1) 가 "1000.0" 으로 반올림 → M 표기로 승격.
  }
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/**
 * 한 턴의 assistant 메시지 (iterations + finalAssistantMessage) 들의 토큰 합계.
 * 메시지 중 하나라도 'estimated' 이면 turn 전체를 'estimated' 로 표시 (보수적).
 * llmCallCount = 토큰 기록 여부와 무관한 assistant 메시지 총 개수 (LLM 콜 횟수).
 * coverage = 토큰이 기록된 메시지 수 / 전체 assistant 메시지 수.
 * cachedInputTokens/cacheCreationTokens = 캐싱 활성 환경에서만 양수 합계.
 */
function aggregateTurnTokens(turn: ChatTurn): {
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  cacheCreationTokens: number;
  source: 'measured' | 'estimated' | 'mixed' | null;
  llmCallCount: number;
  coverage: { measured: number; total: number };
} {
  const msgs: ChatMessage[] = [...turn.iterations];
  if (turn.finalAssistantMessage) msgs.push(turn.finalAssistantMessage);
  let inputTokens = 0;
  let outputTokens = 0;
  let cachedInputTokens = 0;
  let cacheCreationTokens = 0;
  let hasMeasured = false;
  let hasEstimated = false;
  let recordedCount = 0;
  for (const m of msgs) {
    if (m.inputTokens != null) inputTokens += m.inputTokens;
    if (m.outputTokens != null) outputTokens += m.outputTokens;
    if (m.cachedInputTokens != null) cachedInputTokens += m.cachedInputTokens;
    if (m.cacheCreationTokens != null) cacheCreationTokens += m.cacheCreationTokens;
    if (m.tokenSource === 'measured') {
      hasMeasured = true;
      recordedCount++;
    } else if (m.tokenSource === 'estimated') {
      hasEstimated = true;
      recordedCount++;
    }
  }
  const llmCallCount = msgs.length;
  const coverage = { measured: recordedCount, total: llmCallCount };
  if (!hasMeasured && !hasEstimated) {
    return {
      inputTokens,
      outputTokens,
      cachedInputTokens,
      cacheCreationTokens,
      source: null,
      llmCallCount,
      coverage,
    };
  }
  const source: 'measured' | 'estimated' | 'mixed' =
    hasMeasured && hasEstimated ? 'mixed' : hasMeasured ? 'measured' : 'estimated';
  return {
    inputTokens,
    outputTokens,
    cachedInputTokens,
    cacheCreationTokens,
    source,
    llmCallCount,
    coverage,
  };
}

function fmtDatetime(iso: string): string {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function fmtDuration(ms: number): string {
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return '1분 미만';
  if (mins < 60) return `${mins}분`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? `${hours}시간` : `${hours}시간 ${rem}분`;
}

function toDateStr(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

type QuickPreset = 'today' | 'week' | 'lastWeek' | 'month';

function getQuickRange(preset: QuickPreset): { from: string; to: string } {
  const now = new Date();
  const today = toDateStr(now);
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'week' || preset === 'lastWeek') {
    // 'week': 이번 주 월~일. 'lastWeek': 그보다 7일 전체로 시프트.
    // 미래 날짜 데이터는 없으므로 결과적 차이는 없지만 UX 상 "이번 주 = 월~일" 표기.
    const day = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() + (day === 0 ? -6 : 1 - day));
    if (preset === 'lastWeek') monday.setDate(monday.getDate() - 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: toDateStr(monday), to: toDateStr(sunday) };
  }
  // 'month' — 해당 월 1일 ~ 말일. new Date(y, m+1, 0) 가 다음 달 0일 = 이번 달 말일.
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toDateStr(first), to: toDateStr(last) };
}

// ─── 세션 그루핑 (날짜 > 사용자) ─────────────────────────────────────────────

interface UserGroup {
  userId: string;
  email: string | null;
  displayName: string | null;
  sessions: AdminChatSessionSummary[];
}

interface DateGroup {
  date: string; // 'YYYY-MM-DD'
  users: UserGroup[];
}

function groupSessionsByDateAndUser(sessions: AdminChatSessionSummary[]): DateGroup[] {
  const dateMap = new Map<string, Map<string, UserGroup>>();
  for (const s of sessions) {
    const date = toDateStr(new Date(s.createdAt));
    if (!dateMap.has(date)) dateMap.set(date, new Map());
    const userMap = dateMap.get(date);
    if (!userMap) continue;
    const { ownerUserId } = s;
    if (!userMap.has(ownerUserId)) {
      userMap.set(ownerUserId, {
        userId: ownerUserId,
        email: s.ownerEmail,
        displayName: s.ownerDisplayName,
        sessions: [],
      });
    }
    const ug = userMap.get(ownerUserId);
    if (ug) ug.sessions.push(s);
  }
  return Array.from(dateMap.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, userMap]) => ({ date, users: Array.from(userMap.values()) }));
}

// ─── FilterState ─────────────────────────────────────────────────────────────

interface FilterState {
  userIds: string[];
  from: string;
  to: string;
  skillIds: string[];
  llmEndpointIds: string[];
  hasError: boolean;
  hasHitl: boolean;
  agentOnly: boolean;
  desktopOnly: boolean;
  hasBannedWord: boolean;
}

function makeDefaultFilter(): FilterState {
  const { from, to } = getQuickRange('week');
  return {
    userIds: [],
    from,
    to,
    skillIds: [],
    llmEndpointIds: [],
    hasError: false,
    hasHitl: false,
    agentOnly: false,
    desktopOnly: false,
    hasBannedWord: false,
  };
}

function advancedCount(f: FilterState): number {
  return [
    f.userIds.length > 0,
    f.skillIds.length > 0,
    f.llmEndpointIds.length > 0,
    f.hasError,
    f.hasHitl,
    f.agentOnly,
    f.desktopOnly,
    f.hasBannedWord,
  ].filter(Boolean).length;
}

// MultiSelectFilter 는 @/components/ui/multi-select-filter 에서 import.

// ─── AdvancedFilterModal ──────────────────────────────────────────────────────

function AdvancedFilterModal({
  open,
  onClose,
  draft,
  onChange,
  onApply,
  onReset,
}: {
  open: boolean;
  onClose: () => void;
  draft: FilterState;
  onChange: (patch: Partial<FilterState>) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  const skills = useQuery({
    queryKey: ['admin', 'skills'],
    queryFn: () => api.adminSkillList(),
    enabled: open,
  });
  const llms = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: () => api.adminLlmList(),
    enabled: open,
  });
  // 사용자 목록 — 서버 limit/offset 페이지네이션 + search 위임. 클라이언트 substring 필터로는
  // 첫 200 건 안에서만 찾을 수 있으므로 (user-repo.ts:listForAdmin) 검색은 서버로 보내고
  // 스크롤 끝에서 다음 페이지를 lazy-load. selectedUserLabels 로 이미 선택한 사용자의 라벨은
  // 페이지/검색 결과가 좁혀져도 사라지지 않게 캐싱.
  const USER_PAGE_SIZE = 200;
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [selectedUserLabels, setSelectedUserLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserSearch(userSearch), 200);
    return () => clearTimeout(t);
  }, [userSearch]);
  useEffect(() => {
    if (!open) {
      setUserSearch('');
      setDebouncedUserSearch('');
    }
  }, [open]);
  const usersInfinite = useInfiniteQuery({
    queryKey: ['admin', 'users', 'infinite', debouncedUserSearch],
    queryFn: ({ pageParam }) =>
      api.adminUsersList({
        ...(debouncedUserSearch ? { search: debouncedUserSearch } : {}),
        limit: USER_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.users.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: open,
  });
  const userOptions = useMemo(() => {
    const pages = usersInfinite.data?.pages ?? [];
    return pages.flatMap((p) =>
      p.users.map((u) => ({ id: u.id, label: `${u.displayName} (${u.email})` })),
    );
  }, [usersInfinite.data]);
  const selectedUserExtras = useMemo(
    () => Object.entries(selectedUserLabels).map(([id, label]) => ({ id, label })),
    [selectedUserLabels],
  );
  const handleUserSelectionChange = (ids: string[]) => {
    // 새로 추가된 ID 의 label 을 현재 페이지 옵션에서 lookup → cache. 다음 페이지/검색으로
    // 옵션이 좁혀져도 트리거/체크박스 라벨이 유지된다.
    const additions = ids.filter((id) => !draft.userIds.includes(id));
    if (additions.length > 0) {
      setSelectedUserLabels((prev) => {
        const next = { ...prev };
        for (const id of additions) {
          const opt = userOptions.find((o) => o.id === id);
          if (opt && !next[id]) next[id] = opt.label;
        }
        return next;
      });
    }
    onChange({ userIds: ids });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="고급 필터"
      size="md"
      footer={
        <div className="flex justify-between">
          <Button variant="ghost" size="sm" onClick={onReset}>
            초기화
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              취소
            </Button>
            <Button size="sm" onClick={onApply}>
              적용
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4 py-2">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">사용자</span>
          <MultiSelectFilter
            options={userOptions}
            selected={draft.userIds}
            onChange={handleUserSelectionChange}
            placeholder="사용자 검색…"
            triggerPlaceholder="전체 사용자"
            onSearchChange={setUserSearch}
            onLoadMore={() => usersInfinite.fetchNextPage()}
            hasMore={usersInfinite.hasNextPage}
            isLoadingMore={usersInfinite.isFetchingNextPage}
            selectedExtras={selectedUserExtras}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">스킬</span>
          <MultiSelectFilter
            options={(skills.data?.skills ?? []).map((s) => ({ id: s.id, label: s.name }))}
            selected={draft.skillIds}
            onChange={(ids) => onChange({ skillIds: ids })}
            placeholder="스킬 검색…"
            triggerPlaceholder="전체 스킬"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">LLM 엔드포인트</span>
          <MultiSelectFilter
            options={(llms.data?.endpoints ?? []).map((e) => ({
              id: e.id,
              label: `${e.displayName ?? e.name} (${e.model})`,
            }))}
            selected={draft.llmEndpointIds}
            onChange={(ids) => onChange({ llmEndpointIds: ids })}
            placeholder="엔드포인트 검색…"
            triggerPlaceholder="전체 엔드포인트"
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.hasError}
            onChange={(e) => onChange({ hasError: e.target.checked })}
            className="size-4 rounded border-border"
          />
          <span className="text-sm">오류 있는 세션만</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.hasHitl}
            onChange={(e) => onChange({ hasHitl: e.target.checked })}
            className="size-4 rounded border-border"
          />
          <span className="text-sm">HITL 인터럽트 있는 세션만</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.agentOnly}
            onChange={(e) => onChange({ agentOnly: e.target.checked })}
            className="size-4 rounded border-border"
          />
          <span className="text-sm">에이전트(외부 노출) 세션만</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.desktopOnly}
            onChange={(e) => onChange({ desktopOnly: e.target.checked })}
            className="size-4 rounded border-border"
          />
          <span className="text-sm">데스크톱(inniework) 세션만</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.hasBannedWord}
            onChange={(e) => onChange({ hasBannedWord: e.target.checked })}
            className="size-4 rounded border-border"
          />
          <span className="text-sm">금칙어 포함 세션만</span>
        </label>
      </div>
    </Modal>
  );
}

// ─── CopyButton ───────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드 접근 실패 — 무시
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title={copied ? '복사됨' : '복사'}
      aria-label={copied ? '복사됨' : '복사'}
      className="inline-flex items-center justify-center size-5 rounded text-foreground-muted hover:bg-surface-muted hover:text-foreground transition-colors cursor-pointer"
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
    </button>
  );
}

// ─── ToolCallCard ─────────────────────────────────────────────────────────────

/** 출력에 saved_queries.id 가 담기는 툴들. tc.output.queryId 로 추출 가능. */
const SAVED_QUERY_TOOL_KEYS: ReadonlySet<string> = new Set([
  'db.run_query',
  'db.save_query',
  'excel.query',
  'excel.save_query',
]);

function extractSavedQueryId(tc: NonNullable<ChatMessage['toolCalls']>[number]): string | null {
  if (!SAVED_QUERY_TOOL_KEYS.has(tc.toolKey)) return null;
  if (tc.status !== 'ok') return null;
  if (!tc.output || typeof tc.output !== 'object') return null;
  const qid = (tc.output as Record<string, unknown>).queryId;
  return typeof qid === 'string' && qid.length > 0 ? qid : null;
}

/**
 * tool_call 출력의 saved_queries.id 를 "쿼리 보기" 칩으로 노출. 클릭 시 lazy fetch 후
 * SavedQueryDetailModal 을 같은 페이지에서 띄움. 삭제 시 trace 쿼리 invalidate.
 */
function SavedQueryChip({ queryId }: { queryId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'saved-query', queryId],
    queryFn: () => api.adminGetSavedQuery(queryId),
    enabled: open,
  });

  const deleteMut = useMutation({
    mutationFn: (vars: { id: string; confirmActive: boolean }) =>
      api.adminDeleteSavedQuery(vars.id, { confirmActive: vars.confirmActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'chat-session-trace'] });
    },
  });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded border border-border bg-surface-muted/40 px-1.5 py-0.5 font-mono text-[11px] text-foreground-muted cursor-pointer hover:bg-surface-muted hover:text-foreground transition-colors shrink-0"
        title={`쿼리 보기 (${queryId})`}
      >
        <Code className="size-3" />
        쿼리
      </button>
      {open && query.data ? (
        <SavedQueryDetailModal
          item={query.data}
          onClose={() => setOpen(false)}
          onDeleted={() => setOpen(false)}
          deleteMut={deleteMut}
        />
      ) : null}
    </>
  );
}

function ToolCallCard({ tc }: { tc: NonNullable<ChatMessage['toolCalls']>[number] }) {
  const [open, setOpen] = useState(false);
  const isError = tc.status === 'error';
  const inputText = JSON.stringify(tc.input, null, 2);
  const outputText = isError ? (tc.error ?? '') : JSON.stringify(tc.output, null, 2);
  const savedQueryId = extractSavedQueryId(tc);
  return (
    <div className="rounded-md border border-border text-xs">
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex flex-1 min-w-0 items-center gap-2 px-3 py-2 text-left hover:bg-surface-hover cursor-pointer"
        >
          {isError ? (
            <AlertCircle className="size-3.5 text-destructive shrink-0" />
          ) : (
            <CheckCircle2 className="size-3.5 text-[hsl(142_71%_45%)] shrink-0" />
          )}
          <span className="font-mono font-medium truncate flex-1">{tc.toolKey}</span>
          <span className="text-foreground-muted shrink-0">{fmtMs(tc.latencyMs)}</span>
          <span className="text-foreground-muted shrink-0 truncate max-w-56">{tc.summary}</span>
          {open ? (
            <ChevronDown className="size-3.5 shrink-0 text-foreground-muted" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 text-foreground-muted" />
          )}
        </button>
        {savedQueryId ? (
          <div className="pr-3">
            <SavedQueryChip queryId={savedQueryId} />
          </div>
        ) : null}
      </div>
      {open && (
        <div className="border-t border-border flex divide-x divide-border">
          <div className="flex-1 min-w-0 px-3 pb-3 pt-2">
            <div className="flex items-center gap-1.5 mb-1">
              <p className="text-foreground-muted">입력</p>
              <CopyButton text={inputText} />
            </div>
            <pre className="whitespace-pre-wrap break-all bg-surface-hover rounded p-2 text-[11px] max-h-60 overflow-y-auto">
              {inputText}
            </pre>
          </div>
          <div className="flex-1 min-w-0 px-3 pb-3 pt-2">
            <div className="flex items-center gap-1.5 mb-1">
              <p className={isError ? 'text-destructive' : 'text-foreground-muted'}>
                {isError ? '오류' : '출력'}
              </p>
              <CopyButton text={outputText} />
            </div>
            <pre
              className={cn(
                'whitespace-pre-wrap break-all rounded p-2 text-[11px] max-h-60 overflow-y-auto',
                isError ? 'bg-destructive/10' : 'bg-surface-hover',
              )}
            >
              {outputText}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HitlBlock ───────────────────────────────────────────────────────────────

function HitlBlock({ interrupt }: { interrupt: AdminChatInterrupt }) {
  const waitMs = interrupt.resolvedAt
    ? new Date(interrupt.resolvedAt).getTime() - new Date(interrupt.createdAt).getTime()
    : null;
  const resolution = interrupt.resolution;

  return (
    <div className="rounded-md border border-amber-400/40 bg-amber-50/5 p-3 text-xs flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <MessageSquare className="size-3.5 text-amber-500 shrink-0" />
        <span className="font-medium text-amber-600 dark:text-amber-400">
          HITL — {interrupt.kind}
        </span>
        {interrupt.status === 'resolved' && waitMs != null && (
          <span className="text-foreground-muted ml-auto">대기 {fmtMs(waitMs)}</span>
        )}
        {interrupt.status === 'canceled' && (
          <span className="text-destructive ml-auto">취소됨</span>
        )}
        {interrupt.status === 'pending' && (
          <span className="text-foreground-muted ml-auto">응답 대기 중</span>
        )}
      </div>
      <p className="font-medium">{interrupt.prompt.title}</p>
      <p className="text-foreground-muted">{interrupt.prompt.prompt}</p>

      {/* skill_select: 선택지 목록 */}
      {interrupt.prompt.choices && interrupt.prompt.choices.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {interrupt.prompt.choices.map((c) => (
            <span
              key={c.id}
              className={cn(
                'px-2 py-0.5 rounded-full border text-[11px]',
                resolution && 'choiceId' in resolution && resolution.choiceId === c.id
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-border text-foreground-muted',
              )}
            >
              {c.label}
            </span>
          ))}
        </div>
      )}

      {/* ask_user (free text) 답변 */}
      {resolution && 'text' in resolution && resolution.text && (
        <p className="bg-surface-hover rounded p-2">{resolution.text}</p>
      )}

      {/* batch_ask_user: 질문 목록 + 각 질문별 답변 */}
      {interrupt.prompt.questions && interrupt.prompt.questions.length > 0 && (
        <div className="flex flex-col gap-3 pt-1">
          {interrupt.prompt.questions.map((q) => {
            const answer =
              resolution && 'answers' in resolution
                ? resolution.answers.find((a) => a.questionId === q.id)
                : undefined;
            return (
              <div key={q.id} className="flex flex-col gap-1.5">
                <p className="font-medium text-foreground">{q.title}</p>
                {q.kind !== 'free' && q.choices && q.choices.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {q.choices.map((c) => {
                      const selected =
                        (answer?.kind === 'single' && answer.choiceId === c.id) ||
                        (answer?.kind === 'multi' && answer.choiceIds?.includes(c.id));
                      return (
                        <span
                          key={c.id}
                          className={cn(
                            'px-2 py-0.5 rounded-full border text-[11px]',
                            selected
                              ? 'border-primary bg-primary/10 text-primary font-medium'
                              : 'border-border text-foreground-muted',
                          )}
                        >
                          {c.label}
                        </span>
                      );
                    })}
                  </div>
                )}
                {q.kind === 'free' && answer?.text && (
                  <p className="bg-surface-hover rounded p-2 leading-relaxed">{answer.text}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── TurnErrorBlock ──────────────────────────────────────────────────────────

const PHASE_LABEL: Record<NonNullable<AdminChatTurnError['phase']>, string> = {
  llm_stream: 'LLM 스트림',
  run: 'chat run',
  attachment_ingest: '첨부 ingest',
};

function TurnErrorBlock({ error }: { error: AdminChatTurnError }) {
  const [expanded, setExpanded] = useState(false);
  const copyText = useMemo(() => JSON.stringify(error, null, 2), [error]);
  const phaseLabel = error.phase ? PHASE_LABEL[error.phase] : '오류';
  const at = fmtDatetime(error.createdAt);

  return (
    <div className="rounded-md border-l-2 border-destructive bg-destructive/10 p-3 text-xs flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <AlertCircle className="size-3.5 text-destructive shrink-0" />
        <span className="font-medium text-destructive">{phaseLabel} 오류</span>
        <span className="text-foreground-muted">{at}</span>
        {error.model && (
          <span className="px-1.5 py-0.5 rounded bg-surface-muted text-[10px] font-mono">
            {error.model}
          </span>
        )}
        {typeof error.iteration === 'number' && (
          <span className="text-foreground-muted">iter {error.iteration}</span>
        )}
        {error.lastToolKey && (
          <span className="text-foreground-muted">last tool: {error.lastToolKey}</span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <CopyButton text={copyText} />
        </div>
      </div>
      {error.errorMessage && (
        <pre className="text-xs font-mono whitespace-pre-wrap wrap-break-word bg-surface-muted/50 rounded p-2 leading-relaxed">
          {error.errorName ? `${error.errorName}: ` : ''}
          {error.errorMessage}
        </pre>
      )}
      {(error.errorStack ||
        error.baseUrl ||
        error.firstUserMessageHead ||
        (error.skillIds && error.skillIds.length > 0)) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="self-start inline-flex items-center gap-1 text-foreground-muted hover:text-foreground cursor-pointer"
        >
          {expanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          상세
        </button>
      )}
      {expanded && (
        <div className="flex flex-col gap-1.5 pt-1">
          {error.baseUrl && (
            <div className="flex gap-2">
              <span className="text-foreground-muted shrink-0">baseUrl</span>
              <span className="font-mono break-all">{error.baseUrl}</span>
            </div>
          )}
          {error.firstUserMessageHead && (
            <div className="flex gap-2">
              <span className="text-foreground-muted shrink-0">사용자 발화</span>
              <span className="break-all">{error.firstUserMessageHead}</span>
            </div>
          )}
          {error.skillIds && error.skillIds.length > 0 && (
            <div className="flex gap-2">
              <span className="text-foreground-muted shrink-0">skillIds</span>
              <span className="font-mono break-all">{error.skillIds.join(', ')}</span>
            </div>
          )}
          {error.errorStack && (
            <details>
              <summary className="text-foreground-muted cursor-pointer">스택</summary>
              <pre className="mt-1 text-[11px] font-mono whitespace-pre-wrap wrap-break-word bg-surface-muted/40 rounded p-2 max-h-64 overflow-auto">
                {error.errorStack}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CompactionBanner ─────────────────────────────────────────────────────────

function CompactionBanner({ event }: { event: CompactionEvent }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-xs text-foreground-muted">
        <div className="flex-1 h-px bg-border" />
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-surface-hover hover:bg-surface cursor-pointer transition-colors"
        >
          <Archive className="size-3" />
          <span className="font-medium text-foreground">컨텍스트 압축</span>
          <span>·</span>
          <span>{event.summarizedCount}개 메시지</span>
          <span>·</span>
          <span>{event.summaryChars.toLocaleString()}자 요약</span>
          {expanded ? (
            <ChevronUp className="size-3 ml-0.5" />
          ) : (
            <ChevronDown className="size-3 ml-0.5" />
          )}
        </button>
        <div className="flex-1 h-px bg-border" />
      </div>
      {expanded && (
        <div className="px-4 py-3 rounded-md border border-border bg-surface-muted text-sm whitespace-pre-wrap wrap-break-word">
          {event.content}
        </div>
      )}
    </div>
  );
}

// ─── AttachedFilesList ───────────────────────────────────────────────────────

/**
 * 사용자 메시지의 attached_file_ids 를 칩 목록으로 노출. 클릭 시 fileId 로 lazy fetch 후
 * 같은 페이지에서 FileUploadDetailModal 을 띄운다. 삭제 시 trace 쿼리 invalidate 로 본 패널 갱신.
 */
function AttachedFilesList({ fileIds }: { fileIds: string[] }) {
  const qc = useQueryClient();
  const [openFileId, setOpenFileId] = useState<string | null>(null);

  const fileQuery = useQuery({
    queryKey: ['admin', 'file-upload', openFileId],
    queryFn: () => api.adminGetFileUpload(openFileId as string),
    enabled: !!openFileId,
  });

  const deleteMut = useMutation({
    mutationFn: (vars: { id: string; confirmActive: boolean }) =>
      api.adminDeleteFileUpload(vars.id, { confirmActive: vars.confirmActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'chat-session-trace'] });
    },
  });

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-foreground-muted">
      <span>첨부 {fileIds.length}개:</span>
      {fileIds.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => setOpenFileId(id)}
          className="inline-flex items-center gap-1 rounded border border-border bg-surface-muted/40 px-1.5 py-0.5 font-mono text-[11px] cursor-pointer hover:bg-surface-muted hover:text-foreground transition-colors"
          title={id}
        >
          <FileText className="size-3" />
          {id.slice(0, 8)}
        </button>
      ))}
      {openFileId && fileQuery.data ? (
        <FileUploadDetailModal
          item={fileQuery.data}
          onClose={() => setOpenFileId(null)}
          onDeleted={() => setOpenFileId(null)}
          deleteMut={deleteMut}
        />
      ) : null}
      {openFileId && fileQuery.error ? (
        <span className="text-destructive">
          첨부파일 로드 실패{' '}
          <button
            type="button"
            onClick={() => setOpenFileId(null)}
            className="underline cursor-pointer"
          >
            닫기
          </button>
        </span>
      ) : null}
    </div>
  );
}

// ─── TurnCard ────────────────────────────────────────────────────────────────

function TurnCard({
  turn,
  onOpenAgentLog,
  onArtifactClick,
}: {
  turn: ChatTurn;
  onOpenAgentLog: (turnIndex: number) => void;
  onArtifactClick: (art: AdminChatArtifactSummary) => void;
}) {
  const tokens = aggregateTurnTokens(turn);
  const tokenLabel =
    tokens.source != null
      ? `${tokens.source === 'measured' ? '' : '~'}${fmtTokens(tokens.inputTokens)} in / ${fmtTokens(tokens.outputTokens)} out`
      : null;
  const tokenTitle =
    tokens.source != null
      ? buildTokenTooltip({
          inputTokens: tokens.inputTokens,
          outputTokens: tokens.outputTokens,
          source: tokens.source,
          llmCallCount: tokens.llmCallCount,
          coverage: tokens.coverage,
          cachedInputTokens: tokens.cachedInputTokens,
          cacheCreationTokens: tokens.cacheCreationTokens,
        })
      : undefined;

  const externalRef = useMemo(() => getTurnExternalRef(turn), [turn]);
  return (
    <div
      className={cn(
        'rounded-lg border border-border overflow-hidden',
        turn.isArchived && 'opacity-60',
      )}
    >
      <div className="flex items-center gap-3 bg-surface-hover px-4 py-2.5 text-xs">
        <span className="font-semibold text-foreground">Turn {turn.index + 1}</span>
        <span className="text-foreground-muted">{fmtDatetime(turn.createdAt)}</span>
        {turn.isArchived && (
          <span className="flex items-center gap-1 text-foreground-muted">
            <Archive className="size-3" />
            압축됨
          </span>
        )}
        {tokenLabel && (
          <span
            className={cn(
              'ml-auto text-foreground-muted tabular-nums',
              tokens.source !== 'measured' && 'italic',
            )}
            title={tokenTitle}
          >
            {tokenLabel}
          </span>
        )}
        {turn.responseTimeMs != null && (
          <span className={cn('text-foreground-muted', !tokenLabel && 'ml-auto')}>
            {fmtMs(turn.responseTimeMs)}
          </span>
        )}
        {turn.hasError && (
          <span className="flex items-center gap-1 text-destructive font-medium">
            <AlertCircle className="size-3" />
            오류
          </span>
        )}
        {turn.interrupts.length > 0 && (
          <span className="flex items-center gap-1 text-amber-500">
            <MessageSquare className="size-3" />
            HITL
          </span>
        )}
        {externalRef ? (
          <button
            type="button"
            onClick={() => onOpenAgentLog(turn.index)}
            className={cn(
              'flex items-center gap-1 px-1.5 py-0.5 rounded border border-border text-foreground-muted hover:text-foreground hover:bg-surface cursor-pointer',
              externalRef.inferred && 'italic',
            )}
            title={
              externalRef.inferred
                ? '추정된 row_id (verify_tag 실패로 추정)'
                : '에이전트 측 LLM 호출 로그 확인'
            }
          >
            <ClipboardList className="size-3" />
            에이전트 로그{externalRef.inferred ? ' (추정)' : ''}
          </button>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 p-4">
        {turn.userMessage && (
          <div className="flex gap-2">
            <div className="size-6 rounded-full bg-surface-hover border border-border flex items-center justify-center shrink-0 mt-0.5">
              <User className="size-3.5 text-foreground-muted" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground-muted mb-1">사용자</p>
              <p className="text-sm whitespace-pre-wrap wrap-break-word">
                {turn.userMessage.content}
              </p>
              {turn.userMessage.attachedFileIds && turn.userMessage.attachedFileIds.length > 0 && (
                <AttachedFilesList fileIds={turn.userMessage.attachedFileIds} />
              )}
            </div>
          </div>
        )}

        {turn.iterations.map((iter) => (
          <div key={iter.id} className="flex flex-col gap-1.5">
            {iter.toolCalls?.map((tc) => (
              <ToolCallCard key={tc.id} tc={tc} />
            ))}
          </div>
        ))}

        {turn.interrupts.map((intr) => (
          <HitlBlock key={intr.id} interrupt={intr} />
        ))}

        {turn.finalAssistantMessage && (
          <div className="flex gap-2">
            <div className="size-6 rounded-full bg-surface-hover border border-border flex items-center justify-center shrink-0 mt-0.5">
              <Bot className="size-3.5 text-foreground-muted" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground-muted mb-1">어시스턴트</p>
              <Markdown
                content={turn.finalAssistantMessage.content}
                className={cn('text-sm', turn.finalAssistantMessage.archivedAt && 'opacity-50')}
              />
            </div>
          </div>
        )}

        {turn.turnErrors.map((e) => (
          <TurnErrorBlock key={e.id} error={e} />
        ))}

        {turn.artifacts.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {turn.artifacts.map((art) => (
              <button
                type="button"
                key={art.id}
                onClick={() => onArtifactClick(art)}
                title="대시보드/아티팩트 열기"
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-surface-hover border border-border text-xs text-foreground hover:bg-surface hover:border-accent cursor-pointer"
              >
                <FileText className="size-3 text-foreground-muted" />
                {art.title || art.kind}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TraceSessionHeader ───────────────────────────────────────────────────────

function TraceSessionHeader({ trace }: { trace: AdminChatSessionTrace }) {
  const createdAt = new Date(trace.createdAt);
  const updatedAt = new Date(trace.updatedAt);
  const durationMs = updatedAt.getTime() - createdAt.getTime();
  const isSameDay =
    createdAt.getFullYear() === updatedAt.getFullYear() &&
    createdAt.getMonth() === updatedAt.getMonth() &&
    createdAt.getDate() === updatedAt.getDate();

  // 세션 합계 토큰 — 모든 assistant 메시지의 토큰을 합산. 출처는 mixed/measured/estimated 로 표시.
  // llmCallCount = 전체 assistant 메시지 수 (LLM 콜 횟수), coverage = 토큰 기록된 수 / 전체.
  let totalInputTokens = 0;
  let totalOutputTokens = 0;
  let totalCachedInputTokens = 0;
  let totalCacheCreationTokens = 0;
  let hasMeasured = false;
  let hasEstimated = false;
  let totalAssistantMessages = 0;
  let recordedCount = 0;
  for (const m of trace.messages) {
    if (m.role !== 'assistant') continue;
    totalAssistantMessages++;
    if (m.inputTokens != null) totalInputTokens += m.inputTokens;
    if (m.outputTokens != null) totalOutputTokens += m.outputTokens;
    if (m.cachedInputTokens != null) totalCachedInputTokens += m.cachedInputTokens;
    if (m.cacheCreationTokens != null) totalCacheCreationTokens += m.cacheCreationTokens;
    if (m.tokenSource === 'measured') {
      hasMeasured = true;
      recordedCount++;
    } else if (m.tokenSource === 'estimated') {
      hasEstimated = true;
      recordedCount++;
    }
  }
  const tokenSource: 'measured' | 'estimated' | 'mixed' | null =
    hasMeasured && hasEstimated
      ? 'mixed'
      : hasMeasured
        ? 'measured'
        : hasEstimated
          ? 'estimated'
          : null;
  const tokenLabel =
    tokenSource != null
      ? `${tokenSource === 'measured' ? '' : '~'}${fmtTokens(totalInputTokens)} in / ${fmtTokens(totalOutputTokens)} out`
      : null;
  const tokenTitle =
    tokenSource != null
      ? buildTokenTooltip({
          inputTokens: totalInputTokens,
          outputTokens: totalOutputTokens,
          source: tokenSource,
          llmCallCount: totalAssistantMessages,
          coverage: { measured: recordedCount, total: totalAssistantMessages },
          cachedInputTokens: totalCachedInputTokens,
          cacheCreationTokens: totalCacheCreationTokens,
        })
      : undefined;

  return (
    <div className="border-b border-border px-6 py-4 flex items-start gap-3 shrink-0">
      {/* 좌: 제목 + 메타 */}
      <div className="flex-1 min-w-0 flex flex-col justify-between gap-1.5">
        <h2 className="text-base font-semibold truncate">
          {trace.title}
          {trace.deletedAt && (
            <span className="ml-2 align-middle rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] font-normal uppercase tracking-wide text-destructive">
              삭제됨 · {fmtDatetime(trace.deletedAt)}
            </span>
          )}
        </h2>
        <p className="text-xs text-foreground-muted">
          {trace.ownerDisplayName ?? trace.ownerEmail ?? '알 수 없는 사용자'}
          {trace.ownerDisplayName && trace.ownerEmail ? ` [${trace.ownerEmail}]` : ''}
          {' · '}
          {fmtDatetime(trace.createdAt)}
          {' – '}
          {isSameDay ? fmtTime(trace.updatedAt) : fmtDatetime(trace.updatedAt)}
          {` (${fmtDuration(durationMs)})`}
        </p>
        <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
          <span>ID: {trace.id}</span>
          <CopyButton text={trace.id} />
        </div>
      </div>
      {/* 우: 통계 배지 1행 + 스킬 배지 2행 */}
      <div className="flex flex-col items-end gap-1.5 text-xs shrink-0">
        <div className="flex flex-wrap justify-end gap-2">
          {trace.llmEndpointName && (
            <span className="px-2 py-1 rounded-md bg-surface-hover border border-border text-foreground-muted">
              {trace.llmEndpointName}
            </span>
          )}
          {trace.extendedThinking && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-400/30">
              <Zap className="size-3" />
              Extended Thinking
            </span>
          )}
          {trace.agentId && (
            <span
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-surface-hover border border-border text-foreground-muted"
              title={trace.agentName ? `에이전트: ${trace.agentName}` : '에이전트'}
            >
              <Bot className="size-3" />
              에이전트{trace.agentName ? ` · ${trace.agentName}` : ''}
            </span>
          )}
          {trace.clientOrigin === 'desktop' && (
            <span
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-surface-hover border border-border text-foreground-muted"
              title="inniework 데스크톱에서 실행된 세션"
            >
              <Monitor className="size-3" />
              데스크톱
            </span>
          )}
          <span className="px-2 py-1 rounded-md bg-surface-hover border border-border text-foreground-muted">
            {trace.turnCount}턴
          </span>
          {tokenLabel && (
            <span
              className={cn(
                'px-2 py-1 rounded-md bg-surface-hover border border-border text-foreground-muted tabular-nums',
                tokenSource !== 'measured' && 'italic',
              )}
              title={tokenTitle}
            >
              {tokenLabel}
            </span>
          )}
          {trace.errorCount > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-destructive/10 text-destructive border border-destructive/20">
              <AlertCircle className="size-3" />
              오류 {trace.errorCount}
            </span>
          )}
          {trace.hasHitl && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-400/30">
              <MessageSquare className="size-3" />
              HITL
            </span>
          )}
        </div>
        {trace.skillNames.length > 0 && (
          <div className="flex flex-wrap justify-end gap-1.5">
            {trace.skillNames.map((name) => (
              <span
                key={name}
                className="px-2 py-1 rounded-md bg-primary/10 text-primary font-medium"
              >
                {name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TurnNavItem ─────────────────────────────────────────────────────────────

function TurnNavItem({
  turn,
  isSelected,
  onClick,
  navRef,
}: {
  turn: ChatTurn;
  isSelected: boolean;
  onClick: () => void;
  navRef: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div className="relative" ref={navRef}>
      {isSelected && (
        <span
          className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r-full bg-accent"
          aria-hidden
        />
      )}
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex items-center gap-1.5 w-full px-3 py-1.5 text-xs text-left cursor-pointer transition-colors',
          isSelected
            ? 'bg-surface text-foreground'
            : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground',
          turn.isArchived && 'opacity-60',
        )}
      >
        <span className="w-8 shrink-0 text-[11px] font-medium">T{turn.index + 1}</span>
        <span className="flex-1 min-w-0 truncate text-[11px]">
          {turn.userMessage?.content ?? '—'}
        </span>
        {Array.isArray(
          (turn.userMessage?.metadata as { bannedWords?: unknown } | null | undefined)?.bannedWords,
        ) ? (
          <ShieldBan className="size-3 text-destructive shrink-0" aria-label="금칙어 포함" />
        ) : null}
        {turn.hasError ? (
          <span className="size-1.5 rounded-full bg-destructive shrink-0" title="오류" />
        ) : turn.interrupts.length > 0 ? (
          <span className="size-1.5 rounded-full bg-amber-400 shrink-0" title="HITL" />
        ) : (
          <span className="size-1.5 rounded-full bg-[hsl(142_71%_45%)] shrink-0" />
        )}
        {turn.responseTimeMs != null && (
          <span className="shrink-0 text-[11px]">{fmtMs(turn.responseTimeMs)}</span>
        )}
      </button>
    </div>
  );
}

// ─── SessionTracePanel ────────────────────────────────────────────────────────

function SessionTracePanel({ sessionId }: { sessionId: string | null }) {
  const [selectedTurnIndex, setSelectedTurnIndex] = useState<number | null>(null);
  // 트레이스 칩에서 연 artifact (대시보드) 검토 패널 상태.
  const [selectedArtifact, setSelectedArtifact] = useState<AdminChatArtifactSummary | null>(null);
  const [artifactVersion, setArtifactVersion] = useState<number | null>(null);
  const [artifactFullscreen, setArtifactFullscreen] = useState(false);
  const turnRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const navItemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isClickScrollRef = useRef(false);
  const isScrollingRef = useRef(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sessionId 변경 시 ref·상태 초기화
  useEffect(() => {
    setSelectedTurnIndex(null);
    setSelectedArtifact(null);
    setArtifactVersion(null);
    setArtifactFullscreen(false);
    turnRefs.current.clear();
    navItemRefs.current.clear();
    isClickScrollRef.current = false;
    isScrollingRef.current = false;
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, [sessionId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'chat-session-trace', sessionId],
    queryFn: () => api.adminGetChatSessionTrace(sessionId as string),
    enabled: sessionId != null,
    refetchInterval: 10_000,
    refetchOnWindowFocus: false,
  });

  const traceItems = useMemo(
    () =>
      data
        ? groupMessagesIntoTraceItems(
            data.messages,
            data.interrupts,
            data.artifacts,
            data.turnErrors,
          )
        : [],
    [data],
  );
  const turns = useMemo(
    () => traceItems.filter((it): it is ChatTurn => it.kind === 'turn'),
    [traceItems],
  );

  // ─── artifact (대시보드) 검토 패널 ───
  const effectiveArtifactVersion = selectedArtifact
    ? (artifactVersion ?? selectedArtifact.currentVersion)
    : null;
  const artifactContentQuery = useQuery({
    queryKey: [
      'admin',
      'artifact-version',
      sessionId,
      selectedArtifact?.id,
      effectiveArtifactVersion,
    ],
    queryFn: () =>
      sessionId != null && selectedArtifact != null && effectiveArtifactVersion !== null
        ? api.adminArtifactGetVersion(sessionId, selectedArtifact.id, effectiveArtifactVersion)
        : Promise.resolve(null),
    enabled: sessionId != null && selectedArtifact != null && effectiveArtifactVersion !== null,
    staleTime: 60_000,
  });
  const handleArtifactClick = useCallback((art: AdminChatArtifactSummary) => {
    setSelectedArtifact(art);
    setArtifactVersion(null);
    setArtifactFullscreen(false);
  }, []);
  // 대시보드 팝업이 열려 있을 때 Esc 로 닫기.
  useEffect(() => {
    if (!selectedArtifact) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setSelectedArtifact(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedArtifact]);
  // ArtifactPanel 은 Artifact 형태를 요구 — 트레이스 요약 + 세션 id 로 구성 (updatedAt 은 createdAt 으로).
  const artifactForPanel: Artifact | null =
    selectedArtifact && sessionId
      ? {
          id: selectedArtifact.id,
          sessionId,
          kind: selectedArtifact.kind,
          title: selectedArtifact.title,
          currentVersion: selectedArtifact.currentVersion,
          createdAt: selectedArtifact.createdAt,
          updatedAt: selectedArtifact.createdAt,
        }
      : null;

  // 에이전트 로그 모달 — 로그 있는 턴들만 추려 prev/next 네비게이션 가능하도록.
  const loggedTurns = useMemo(
    () =>
      turns
        .map((t) => {
          const externalRef = getTurnExternalRef(t);
          if (!externalRef) return null;
          return {
            turnIndex: t.index,
            externalRef,
            userQuestion: t.userMessage?.content ?? null,
          };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null),
    [turns],
  );
  const [agentLogTurnIndex, setAgentLogTurnIndex] = useState<number | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: sessionId 변경 시 모달 닫기
  useEffect(() => {
    setAgentLogTurnIndex(null);
  }, [sessionId]);
  const currentLoggedPos = useMemo(() => {
    if (agentLogTurnIndex == null) return -1;
    return loggedTurns.findIndex((lt) => lt.turnIndex === agentLogTurnIndex);
  }, [agentLogTurnIndex, loggedTurns]);
  const currentLogged = currentLoggedPos >= 0 ? loggedTurns[currentLoggedPos] : null;
  const handleOpenAgentLog = useCallback((turnIndex: number) => {
    setAgentLogTurnIndex(turnIndex);
  }, []);
  const handleCloseAgentLog = useCallback(() => {
    setAgentLogTurnIndex(null);
  }, []);
  const handlePrevAgentLog = useCallback(() => {
    if (currentLoggedPos <= 0) return;
    const prev = loggedTurns[currentLoggedPos - 1];
    if (prev) setAgentLogTurnIndex(prev.turnIndex);
  }, [currentLoggedPos, loggedTurns]);
  const handleNextAgentLog = useCallback(() => {
    if (currentLoggedPos < 0 || currentLoggedPos >= loggedTurns.length - 1) return;
    const next = loggedTurns[currentLoggedPos + 1];
    if (next) setAgentLogTurnIndex(next.turnIndex);
  }, [currentLoggedPos, loggedTurns]);

  // 클릭 시에만 메인 패널 스크롤 실행 — gap-4(16px)보다 작은 8px 패딩으로 이전 카드가 뷰 밖에 위치
  useEffect(() => {
    if (selectedTurnIndex == null || !isClickScrollRef.current) return;
    isClickScrollRef.current = false;
    const el = turnRefs.current.get(selectedTurnIndex);
    const container = scrollContainerRef.current;
    if (!el || !container) return;
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const targetScrollTop = container.scrollTop + elRect.top - containerRect.top - 8;
    container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
  }, [selectedTurnIndex]);

  // 활성 턴 nav 항목이 nav 패널 안에서 보이도록 자동 스크롤
  useEffect(() => {
    if (selectedTurnIndex == null) return;
    navItemRefs.current.get(selectedTurnIndex)?.scrollIntoView({ block: 'nearest' });
  }, [selectedTurnIndex]);

  // 스크롤 스파이: 스크롤 컨테이너 상단 40% 구간에 진입한 가장 앞 턴을 활성화
  // visibleSet을 항상 갱신해야 stale 방지 — isScrollingRef 억제 제거
  // biome-ignore lint/correctness/useExhaustiveDependencies: turnRefs는 mutable ref
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !turns.length) return;

    const visibleSet = new Set<number>();

    const observer = new IntersectionObserver(
      (entries) => {
        // visibleSet은 항상 갱신 (stale 방지)
        for (const entry of entries) {
          const idx = [...turnRefs.current.entries()].find(([, el]) => el === entry.target)?.[0];
          if (idx == null) continue;
          if (entry.isIntersecting) visibleSet.add(idx);
          else visibleSet.delete(idx);
        }
        // 클릭 스크롤 중에는 nav 하이라이트만 억제
        if (isScrollingRef.current) return;
        if (visibleSet.size > 0) setSelectedTurnIndex(Math.min(...visibleSet));
      },
      { root: container, rootMargin: '0px 0px -60% 0px', threshold: 0.1 },
    );

    for (const [, el] of turnRefs.current) observer.observe(el);
    return () => observer.disconnect();
  }, [sessionId, turns.length]);

  const handleNavClick = useCallback((index: number) => {
    isClickScrollRef.current = true;
    isScrollingRef.current = true;
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 800);
    setSelectedTurnIndex(index);
  }, []);

  if (!sessionId) {
    return (
      <div className="flex-1 flex items-center justify-center text-foreground-muted text-sm">
        ← 세션을 선택해주세요
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-foreground-muted text-sm">
        불러오는 중…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex-1 flex items-center justify-center text-destructive text-sm">
        {error instanceof ApiError ? error.message : '불러오기 실패'}
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <TraceSessionHeader trace={data} />
      <div className="flex-1 flex overflow-hidden">
        {/* 좌측 턴 네비게이션 */}
        <div className="w-72 shrink-0 border-r border-border flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            {traceItems.map((item) => {
              if (item.kind === 'turn') {
                return (
                  <TurnNavItem
                    key={`turn-${item.index}`}
                    turn={item}
                    isSelected={selectedTurnIndex === item.index}
                    onClick={() => handleNavClick(item.index)}
                    navRef={(el) => {
                      if (el) navItemRefs.current.set(item.index, el);
                      else navItemRefs.current.delete(item.index);
                    }}
                  />
                );
              }
              return (
                <div
                  key={`compaction-${item.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-foreground-muted select-none"
                >
                  <div className="flex-1 h-px bg-border" />
                  <Archive className="size-2.5" />
                  <span>압축</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              );
            })}
          </div>
        </div>

        {/* 우측 턴 상세 */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* min-h-0 필수: flex-1 자식에서 overflow-y-auto가 실제 스크롤 컨텍스트를 생성하려면 */}
          <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto">
            <div className="flex flex-col gap-4 px-6 py-4">
              {turns.length === 0 ? (
                <p className="text-sm text-foreground-muted text-center py-8">메시지가 없습니다.</p>
              ) : (
                <>
                  {traceItems.map((item) => {
                    if (item.kind === 'turn') {
                      return (
                        <div
                          key={`turn-${item.index}`}
                          ref={(el) => {
                            if (el) turnRefs.current.set(item.index, el);
                            else turnRefs.current.delete(item.index);
                          }}
                        >
                          <TurnCard
                            turn={item}
                            onOpenAgentLog={handleOpenAgentLog}
                            onArtifactClick={handleArtifactClick}
                          />
                        </div>
                      );
                    }
                    return <CompactionBanner key={`compaction-${item.id}`} event={item} />;
                  })}
                  <div className="flex items-center gap-3 py-2 text-xs text-foreground-muted select-none">
                    <div className="flex-1 h-px bg-border" />
                    <span className="flex items-center gap-1.5">
                      <History className="size-3" />
                      여기까지 기록됨
                    </span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                </>
              )}
            </div>
            {/* 마지막 턴도 상단까지 스크롤될 수 있도록 뷰포트 높이만큼 여백 확보 */}
            <div className="h-screen" aria-hidden />
          </div>
        </div>
      </div>
      {currentLogged ? (
        <InsightAgentLogModal
          open={agentLogTurnIndex != null}
          onClose={handleCloseAgentLog}
          externalRef={currentLogged.externalRef}
          turnIndex={currentLogged.turnIndex}
          userQuestion={currentLogged.userQuestion}
          position={{ current: currentLoggedPos + 1, total: loggedTurns.length }}
          onPrev={currentLoggedPos > 0 ? handlePrevAgentLog : undefined}
          onNext={currentLoggedPos < loggedTurns.length - 1 ? handleNextAgentLog : undefined}
        />
      ) : null}
      {artifactForPanel && sessionId ? (
        <div className="fixed inset-0 z-50 flex p-4">
          {/* 배경 클릭 = 닫기. ArtifactPanel 헤더의 X 로도 닫힌다. */}
          <button
            type="button"
            aria-label="닫기"
            className="absolute inset-0 bg-black/40"
            onClick={() => setSelectedArtifact(null)}
          />
          <div
            className={cn(
              'relative m-auto flex flex-col overflow-hidden bg-surface shadow-xl',
              artifactFullscreen
                ? 'h-full w-full'
                : 'h-[88vh] w-[90vw] max-w-6xl rounded-lg border border-border',
            )}
          >
            <ArtifactPanel
              artifact={artifactForPanel}
              versionContent={artifactContentQuery.data ?? null}
              effectiveVersion={effectiveArtifactVersion}
              isLoading={artifactContentQuery.isLoading || artifactContentQuery.isFetching}
              onClose={() => setSelectedArtifact(null)}
              onSelectVersion={(v) => setArtifactVersion(v)}
              isFullscreen={artifactFullscreen}
              onToggleFullscreen={() => setArtifactFullscreen((f) => !f)}
              rpc={(aid, method, params) => api.adminArtifactRpc(sessionId, aid, method, params)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─── SessionNode ──────────────────────────────────────────────────────────────

function SessionNode({
  session,
  isSelected,
  onSelect,
}: {
  session: AdminChatSessionSummary;
  isSelected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="relative" data-session-id={session.id}>
      {isSelected && (
        <span
          className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r-full bg-accent"
          aria-hidden
        />
      )}
      <button
        type="button"
        onClick={() => onSelect(session.id)}
        className={cn(
          'flex items-center gap-2 w-full pl-10 pr-3 py-1.5 text-xs text-left cursor-pointer transition-colors',
          isSelected
            ? 'bg-surface text-foreground'
            : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground',
        )}
      >
        <span
          className={cn('flex-1 min-w-0 truncate', session.deletedAt && 'line-through opacity-60')}
        >
          {session.title}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {session.agentId && (
            <span
              className="flex items-center text-foreground-muted"
              aria-label="에이전트"
              title={
                session.skillNames.length > 0
                  ? `에이전트${session.agentName ? `: ${session.agentName}` : ''} · 스킬: ${session.skillNames.join(', ')}`
                  : `에이전트${session.agentName ? `: ${session.agentName}` : ''}`
              }
            >
              <Bot className="size-3" />
            </span>
          )}
          {session.clientOrigin === 'desktop' && (
            <span
              className="flex items-center text-foreground-muted"
              aria-label="데스크톱"
              title="inniework 데스크톱에서 실행된 세션"
            >
              <Monitor className="size-3" />
            </span>
          )}
          {session.deletedAt && (
            <span
              className="rounded bg-foreground-muted/15 px-1 text-[9px] uppercase tracking-wide text-foreground-muted"
              title={`사용자가 ${new Date(session.deletedAt).toLocaleString('ko-KR')} 에 삭제`}
            >
              삭제됨
            </span>
          )}
          {session.errorCount > 0 && (
            <span className="size-1.5 rounded-full bg-destructive" title="오류 있음" />
          )}
          {session.hasHitl && (
            <span className="size-1.5 rounded-full bg-amber-400" title="HITL 있음" />
          )}
        </div>
        <span className="text-[10px] text-foreground-muted shrink-0">{session.turnCount}턴</span>
      </button>
    </div>
  );
}

// ─── UserNode ─────────────────────────────────────────────────────────────────

function UserNode({
  date,
  userGroup,
  isExpanded,
  selectedId,
  onToggle,
  onSelect,
}: {
  date: string;
  userGroup: UserGroup;
  isExpanded: boolean;
  selectedId: string | null;
  onToggle: (key: string) => void;
  onSelect: (id: string) => void;
}) {
  const key = `${date}_${userGroup.userId}`;
  const label = userGroup.displayName ?? userGroup.email ?? userGroup.userId.slice(0, 8);

  return (
    <div className="pl-4">
      <button
        type="button"
        onClick={() => onToggle(key)}
        className="flex items-center gap-1.5 w-full px-3 py-1.5 text-xs text-left cursor-pointer text-foreground-muted hover:bg-surface-muted hover:text-foreground transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="size-3 shrink-0" />
        ) : (
          <ChevronRight className="size-3 shrink-0" />
        )}
        <User className="size-3 shrink-0" />
        <span className="flex-1 min-w-0 truncate">{label}</span>
        <span className="text-[10px] text-foreground-muted shrink-0">
          {userGroup.sessions.length}
        </span>
      </button>
      {isExpanded &&
        userGroup.sessions.map((s) => (
          <SessionNode
            key={s.id}
            session={s}
            isSelected={selectedId === s.id}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

// ─── DateNode ─────────────────────────────────────────────────────────────────

function DateNode({
  dateGroup,
  isExpanded,
  expandedUsers,
  selectedId,
  onToggleDate,
  onToggleUser,
  onSelect,
}: {
  dateGroup: DateGroup;
  isExpanded: boolean;
  expandedUsers: Set<string>;
  selectedId: string | null;
  onToggleDate: (date: string) => void;
  onToggleUser: (key: string) => void;
  onSelect: (id: string) => void;
}) {
  const totalSessions = dateGroup.users.reduce((n, u) => n + u.sessions.length, 0);

  return (
    <div>
      <button
        type="button"
        onClick={() => onToggleDate(dateGroup.date)}
        className="flex items-center gap-1.5 w-full px-3 py-2 text-xs text-left cursor-pointer text-foreground-muted hover:bg-surface-muted hover:text-foreground transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="size-3.5 shrink-0" />
        ) : (
          <ChevronRight className="size-3.5 shrink-0" />
        )}
        <CalendarDays className="size-3.5 shrink-0" />
        <span className="flex-1 min-w-0 truncate font-medium">{dateGroup.date}</span>
        <span className="text-[10px] text-foreground-muted shrink-0">{totalSessions}</span>
      </button>
      {isExpanded &&
        dateGroup.users.map((ug) => (
          <UserNode
            key={ug.userId}
            date={dateGroup.date}
            userGroup={ug}
            isExpanded={expandedUsers.has(`${dateGroup.date}_${ug.userId}`)}
            selectedId={selectedId}
            onToggle={onToggleUser}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

// ─── SessionTreeSidebar ───────────────────────────────────────────────────────

function SessionTreeSidebar({
  grouped,
  capped,
  selectedId,
  selectionMissing,
  onClearDeepLinkState,
  expandedDates,
  expandedUsers,
  onToggleDate,
  onToggleUser,
  onSelect,
  searchDraft,
  onSearchChange,
  advancedActiveCount,
  onOpenAdvanced,
  onRefresh,
  isRefreshing,
  onExpandAll,
  onCollapseAll,
}: {
  grouped: DateGroup[];
  capped: boolean;
  selectedId: string | null;
  selectionMissing: boolean;
  onClearDeepLinkState: () => void;
  expandedDates: Set<string>;
  expandedUsers: Set<string>;
  onToggleDate: (date: string) => void;
  onToggleUser: (key: string) => void;
  onSelect: (id: string) => void;
  searchDraft: string;
  onSearchChange: (v: string) => void;
  advancedActiveCount: number;
  onOpenAdvanced: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}) {
  return (
    <div className="w-72 shrink-0 border-r border-border flex flex-col overflow-hidden">
      {/* 검색 + 고급 필터 */}
      <div className="px-3 py-2 border-b border-border flex items-center gap-1.5">
        <div className="relative flex-1">
          <Input
            placeholder="세션 검색…"
            value={searchDraft}
            onChange={(e) => onSearchChange(e.target.value)}
            className={cn('h-8 text-xs', searchDraft && 'pr-7')}
          />
          {searchDraft && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              title="검색 지우기"
              aria-label="검색 지우기"
              className="absolute right-1 top-1/2 -translate-y-1/2 size-5 grid place-items-center rounded text-foreground-muted cursor-pointer hover:bg-surface-muted hover:text-foreground transition-colors"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onOpenAdvanced}
          title="고급 필터"
          className={cn(
            'relative size-8 shrink-0 inline-grid place-items-center rounded-md',
            'text-foreground-muted cursor-pointer hover:bg-surface-muted hover:text-foreground transition-colors',
            advancedActiveCount > 0 && 'text-primary bg-primary/10',
          )}
        >
          <SlidersHorizontal className="size-3.5" />
          {advancedActiveCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
              {advancedActiveCount}
            </span>
          )}
        </button>
      </div>

      {/* 새로고침 + 전체 펼치기 / 닫기 */}
      <div className="px-3 py-1.5 border-b border-border flex items-center justify-between">
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          title="새로고침"
          className="flex items-center gap-1 px-2 py-1 text-[11px] text-foreground-muted cursor-pointer hover:text-foreground hover:bg-surface-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={cn('size-3', isRefreshing && 'animate-spin')} />
          새로고침
        </button>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={onExpandAll}
            className="flex items-center gap-1 px-2 py-1 text-[11px] text-foreground-muted cursor-pointer hover:text-foreground hover:bg-surface-muted rounded transition-colors"
          >
            <ChevronsUpDown className="size-3" />
            펼치기
          </button>
          <button
            type="button"
            onClick={onCollapseAll}
            className="flex items-center gap-1 px-2 py-1 text-[11px] text-foreground-muted cursor-pointer hover:text-foreground hover:bg-surface-muted rounded transition-colors"
          >
            <ChevronsDownUp className="size-3" />
            닫기
          </button>
        </div>
      </div>

      {/* 트리 */}
      <div className="flex-1 overflow-y-auto">
        {capped && (
          <div className="px-3 py-2 flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50/5 border-b border-amber-400/20">
            <AlertCircle className="size-3 shrink-0" />
            500개 초과 — 날짜 범위를 좁혀주세요
          </div>
        )}
        {selectionMissing && (
          <div className="px-3 py-2 flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50/5 border-b border-amber-400/20">
            <AlertCircle className="size-3 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p>선택한 세션이 현재 결과에 없습니다 — 검색 범위 밖일 수 있습니다.</p>
              <button
                type="button"
                onClick={onClearDeepLinkState}
                className="mt-1 underline cursor-pointer hover:text-foreground"
              >
                필터 초기화
              </button>
            </div>
          </div>
        )}
        {grouped.length === 0 && (
          <p className="px-4 py-6 text-xs text-foreground-muted text-center">세션이 없습니다.</p>
        )}
        {grouped.map((dg) => (
          <DateNode
            key={dg.date}
            dateGroup={dg}
            isExpanded={expandedDates.has(dg.date)}
            expandedUsers={expandedUsers}
            selectedId={selectedId}
            onToggleDate={onToggleDate}
            onToggleUser={onToggleUser}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

// ─── ChatLogHeader ────────────────────────────────────────────────────────────

const QUICK_PRESETS: { label: string; preset: QuickPreset }[] = [
  { label: '오늘', preset: 'today' },
  { label: '이번 주', preset: 'week' },
  { label: '지난 주', preset: 'lastWeek' },
  { label: '이번 달', preset: 'month' },
];

function ChatLogHeader({
  total,
  fromDraft,
  toDraft,
  onFromChange,
  onToChange,
  onQuickFilter,
}: {
  total: number;
  fromDraft: string;
  toDraft: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onQuickFilter: (preset: QuickPreset) => void;
}) {
  return (
    <div className="border-b border-border px-10 pt-8 pb-4 shrink-0">
      <PageHeader
        title="채팅 로그"
        count={total}
        description="사용자 채팅 세션 및 도구 호출 흐름"
        actions={
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-muted">기간</span>
              <input
                type="date"
                value={fromDraft}
                onChange={(e) => onFromChange(e.target.value)}
                className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
              />
              <span className="text-xs text-foreground-muted">~</span>
              <input
                type="date"
                value={toDraft}
                onChange={(e) => onToChange(e.target.value)}
                className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
              />
            </div>
            <div className="flex items-center gap-1">
              {QUICK_PRESETS.map(({ label, preset }) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => onQuickFilter(preset)}
                  className="h-6 rounded px-2 text-[11px] text-foreground-muted hover:bg-surface-hover hover:text-foreground transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        }
      />
    </div>
  );
}

// ─── AdminChatLog (메인) ──────────────────────────────────────────────────────

export function AdminChatLog() {
  // 외부 deep-link (FileUploads · SavedQueries 등) 에서 넘어온 sessionId / date
  // ChatLog 진입 시 검색·필터·선택을 자동 세팅한다. 아래 useEffect 가 적용.
  const linkSearch = useSearch({ from: '/admin/chat-log' });
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>(makeDefaultFilter);
  const [draft, setDraft] = useState<FilterState>(makeDefaultFilter);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [isAllExpanded, setIsAllExpanded] = useState(true);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [searchDraft, setSearchDraft] = useState('');
  const [sessionSearch, setSessionSearch] = useState('');

  // 사용자가 필터·검색을 손대면 URL 의 deep-link params 도 제거 — URL 공유 시 현재 화면과
  // 일치하도록. deep-link params 가 비어있으면 navigate skip 으로 불필요한 history push 차단.
  const stripDeepLinkUrl = useCallback(() => {
    if (linkSearch.sessionId || linkSearch.date) {
      navigate({ to: '/admin/chat-log', search: {} });
    }
  }, [linkSearch.sessionId, linkSearch.date, navigate]);

  // 세션 검색 디바운스
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback(
    (v: string) => {
      setSearchDraft(v);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => setSessionSearch(v), 200);
      stripDeepLinkUrl();
    },
    [stripDeepLinkUrl],
  );
  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );

  // Deep-link 진입 시 1회 적용 — sessionId 로 검색 + date 기준 -30일 ~ date 필터.
  // 그 외 advanced 필터(skillIds·llmEndpointIds·hasError 등) 는 default 로 리셋해서
  // 사용자가 의도치 않게 좁힌 필터 때문에 대상 세션을 못 찾는 사고 방지.
  // params 가 바뀔 때마다 다시 적용 (같은 페이지에서 다른 deep-link 진입 케이스).
  useEffect(() => {
    const linkSessionId = linkSearch.sessionId;
    if (!linkSessionId) return;
    setSearchDraft(linkSessionId);
    setSessionSearch(linkSessionId);
    setSelectedSessionId(linkSessionId);
    const base = makeDefaultFilter();
    if (linkSearch.date) {
      const to = linkSearch.date;
      const fromDate = new Date(`${linkSearch.date}T00:00:00`);
      fromDate.setDate(fromDate.getDate() - 30);
      const from = toDateStr(fromDate);
      const next: FilterState = { ...base, from, to };
      setFilters(next);
      setDraft(next);
    } else {
      setFilters(base);
      setDraft(base);
    }
  }, [linkSearch.sessionId, linkSearch.date]);

  // 날짜 필터 즉시 반영
  const handleFromChange = (v: string) => {
    setDraft((d) => ({ ...d, from: v }));
    setFilters((f) => ({ ...f, from: v }));
    stripDeepLinkUrl();
  };
  const handleToChange = (v: string) => {
    setDraft((d) => ({ ...d, to: v }));
    setFilters((f) => ({ ...f, to: v }));
    stripDeepLinkUrl();
  };

  // 세션 목록 쿼리 — 검색은 서버 측에서 title / id 부분일치 OR 매칭.
  // 페이지네이션 너머 세션도 ID 검색으로 찾을 수 있도록 클라이언트 후필터 X.
  const sessionsQuery = useQuery({
    queryKey: ['admin', 'chat-sessions', filters, sessionSearch],
    queryFn: () =>
      api.adminListChatSessions({
        userIds: filters.userIds.length > 0 ? filters.userIds : undefined,
        from: filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : undefined,
        to: filters.to ? new Date(`${filters.to}T23:59:59`).toISOString() : undefined,
        skillIds: filters.skillIds.length > 0 ? filters.skillIds : undefined,
        llmEndpointIds: filters.llmEndpointIds.length > 0 ? filters.llmEndpointIds : undefined,
        hasError: filters.hasError ? 'true' : undefined,
        hasHitl: filters.hasHitl ? 'true' : undefined,
        agentOnly: filters.agentOnly ? 'true' : undefined,
        desktopOnly: filters.desktopOnly ? 'true' : undefined,
        hasBannedWord: filters.hasBannedWord ? 'true' : undefined,
        search: sessionSearch || undefined,
      }),
    refetchOnWindowFocus: false,
  });

  const sessions = sessionsQuery.data?.sessions ?? [];
  const total = sessionsQuery.data?.total ?? 0;
  const capped = sessionsQuery.data?.capped ?? false;

  // 선택된 세션이 현재 결과에 없는 상태 — deep-link 의 30일 buffer 밖이거나 사용자가
  // 필터를 더 좁혀 결과에서 빠진 경우. 우측 패널은 별도 trace 쿼리로 살아있어서
  // "좌측엔 없는데 우측엔 있는" 어색함이 생기므로 안내 배너 노출.
  const selectionMissing =
    !!selectedSessionId &&
    !sessionsQuery.isLoading &&
    !sessions.some((s) => s.id === selectedSessionId);

  // 배너의 "필터 초기화" — 검색·선택·필터·URL params 모두 default 로.
  const handleClearDeepLinkState = useCallback(() => {
    setSearchDraft('');
    setSessionSearch('');
    setSelectedSessionId(null);
    const base = makeDefaultFilter();
    setFilters(base);
    setDraft(base);
    if (linkSearch.sessionId || linkSearch.date) {
      navigate({ to: '/admin/chat-log', search: {} });
    }
  }, [linkSearch.sessionId, linkSearch.date, navigate]);

  const grouped = useMemo(() => groupSessionsByDateAndUser(sessions), [sessions]);

  // 전체 펼치기 모드: grouped가 바뀔 때 새 항목 자동 추가
  useEffect(() => {
    if (!isAllExpanded) return;
    setExpandedDates(new Set(grouped.map((dg) => dg.date)));
    setExpandedUsers(
      new Set(grouped.flatMap((dg) => dg.users.map((ug) => `${dg.date}_${ug.userId}`))),
    );
  }, [isAllExpanded, grouped]);

  // 사이드바에서 선택된 세션을 viewport 안으로 스크롤. deep-link 진입 직후 / 검색 결과로
  // 다른 세션이 선택될 때 모두 동작. block: 'nearest' 라 이미 보이면 스크롤 없음 (멱등).
  useEffect(() => {
    if (!selectedSessionId) return;
    if (sessions.length === 0) return;
    const el = document.querySelector<HTMLElement>(`[data-session-id="${selectedSessionId}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedSessionId, sessions]);

  // 트리 토글
  const handleToggleDate = useCallback((date: string) => {
    setIsAllExpanded(false);
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  }, []);

  const handleToggleUser = useCallback((key: string) => {
    setIsAllExpanded(false);
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    const dates = new Set<string>();
    const users = new Set<string>();
    for (const dg of grouped) {
      dates.add(dg.date);
      for (const ug of dg.users) {
        users.add(`${dg.date}_${ug.userId}`);
      }
    }
    setIsAllExpanded(true);
    setExpandedDates(dates);
    setExpandedUsers(users);
  }, [grouped]);

  const handleCollapseAll = useCallback(() => {
    setIsAllExpanded(false);
    setExpandedDates(new Set());
    setExpandedUsers(new Set());
  }, []);

  const handleQuickFilter = (preset: QuickPreset) => {
    const { from, to } = getQuickRange(preset);
    setDraft((d) => ({ ...d, from, to }));
    setFilters((f) => ({ ...f, from, to }));
    stripDeepLinkUrl();
  };

  const handleApplyAdvanced = () => {
    setFilters((f) => ({
      ...f,
      userIds: draft.userIds,
      skillIds: draft.skillIds,
      llmEndpointIds: draft.llmEndpointIds,
      hasError: draft.hasError,
      hasHitl: draft.hasHitl,
      agentOnly: draft.agentOnly,
      desktopOnly: draft.desktopOnly,
      hasBannedWord: draft.hasBannedWord,
    }));
    setAdvancedOpen(false);
    stripDeepLinkUrl();
  };

  const handleResetAdvanced = () => {
    setDraft((d) => ({
      ...d,
      userIds: [],
      skillIds: [],
      llmEndpointIds: [],
      hasError: false,
      hasHitl: false,
      agentOnly: false,
      desktopOnly: false,
    }));
  };

  return (
    <PageShell contentClassName="p-0 overflow-hidden flex flex-col flex-1">
      <ChatLogHeader
        total={total}
        fromDraft={draft.from}
        toDraft={draft.to}
        onFromChange={handleFromChange}
        onToChange={handleToChange}
        onQuickFilter={handleQuickFilter}
      />

      <div className="flex flex-1 overflow-hidden">
        <SessionTreeSidebar
          grouped={grouped}
          capped={capped}
          selectedId={selectedSessionId}
          selectionMissing={selectionMissing}
          onClearDeepLinkState={handleClearDeepLinkState}
          expandedDates={expandedDates}
          expandedUsers={expandedUsers}
          onToggleDate={handleToggleDate}
          onToggleUser={handleToggleUser}
          onSelect={setSelectedSessionId}
          searchDraft={searchDraft}
          onSearchChange={handleSearchChange}
          advancedActiveCount={advancedCount(filters)}
          onOpenAdvanced={() => {
            setDraft(filters);
            setAdvancedOpen(true);
          }}
          onRefresh={() => sessionsQuery.refetch()}
          isRefreshing={sessionsQuery.isFetching}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
        />

        <SessionTracePanel sessionId={selectedSessionId} />
      </div>

      <AdvancedFilterModal
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        draft={draft}
        onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
        onApply={handleApplyAdvanced}
        onReset={handleResetAdvanced}
      />
    </PageShell>
  );
}
